// import { ScramLoginParams, ScramLoginResponseBody } from '@iotize/tap/auth';
import { bufferToHexString } from '@iotize/common/byte-converter';
import { AesEcb128Converter } from '@iotize/common/crypto';
import { EncryptionOptions } from '@iotize/tap/auth';
import {
  EncryptionAlgo,
  TapRequestFrame,
  TapResponseFrame,
} from '@iotize/tap/client/api';
import {
  CryptedFrameConverter,
  TapStreamReader,
  TAP_RESPONSE_FRAME_CONVERTER,
} from '@iotize/tap/client/impl';
import {
  EncryptedIVRequest,
  EncryptedIVResponse,
  scramConverters,
  ScramLoginParams,
  ScramLoginResponseBody,
} from '@iotize/tap/service/impl/scram';

import { GET } from '../annotations';
import { DEFAULT_HASH_ITERATION } from '../config';
import {
  initScramLoginParam,
  initScramLoginResponse,
  uint32,
} from '../converters';
import { TapServerError } from '../tap-server-error';
import { CallContext, VirtualTap } from '../virtual-tap-server';

const INITIALIZATION_VECTOR_LENGTH = 16;
const DEFAULT_INITIALIZATION_VECTOR = '00000000000000000000000000000000';

function _hex(data: Uint8Array | undefined) {
  return data ? bufferToHexString(data) : '';
}

export class ScramService {
  keepAliveCounter = 0;

  private _options: EncryptionOptions = {
    encryption: false,
    initializationVectorResetPeriod: 100,
    keys: {},
    frameCounter: 0,
  };

  private _encryptedFrameConverter = new CryptedFrameConverter({
    next: () => {
      return this._options.frameCounter++;
    },
  });

  private _encryptionAlgo?: EncryptionAlgo;

  /**
   * Clear encrypted session.
   * Destroys frame counter, session keys and initialization vectors
   * This will also stop encrypted communication if it was running
   */
  private clearSession() {
    // this.pauseEncryption();
    this._options.keys.ivDecode = undefined;
    this._options.keys.ivEncode = undefined;
    this._options.keys.sessionKey = undefined;
  }

  private refreshEncryptionAlgo() {
    if (this._options.keys.sessionKey) {
      const algo = new AesEcb128Converter({
        key: _hex(this._options.keys.sessionKey),
        ivDecode: this._options.keys.ivDecode
          ? _hex(this._options.keys.ivDecode)
          : DEFAULT_INITIALIZATION_VECTOR,
        ivEncode: this._options.keys.ivEncode
          ? _hex(this._options.keys.ivEncode)
          : DEFAULT_INITIALIZATION_VECTOR,
      });
      this._encryptionAlgo = algo;
    }
  }

  private get encryptionAlgo(): EncryptionAlgo {
    if (!this._encryptionAlgo) {
      console.warn('scram session not started yet');
      throw TapServerError.notAvailable();
    }
    return this._encryptionAlgo;
  }

  constructor(public server: VirtualTap) {}

  @GET({
    path: '/1024//40',
    payloadType: initScramLoginParam,
    responseType: initScramLoginResponse,
  })
  public initLogin(context: CallContext<ScramLoginParams>) {
    // this.server.login(context.body);
    const result: ScramLoginResponseBody = {
      iterationNumber: 10,
      salt: new Uint8Array(4),
      serverNonce: 0,
    };
    return result;
  }

  //   @GET({
  //     path: '/1024//40',
  //     payloadType: loginCredential,
  //   })
  //   public loginProof() {
  //   }

  @GET({
    path: '/1024//42',
    responseType: uint32,
  })
  public getScramIteration() {
    return (
      this.server.config.tap?.scram?.hashIteration || DEFAULT_HASH_ITERATION
    );
  }

  @GET({
    path: '/1024//47',
  })
  public scramInitialized() {
    this._options.keys.sessionKey = new Uint8Array(32).fill(0);
    this.refreshEncryptionAlgo();
    return this._options.keys.sessionKey;
  }

  private _decodeEncryptedFrame(frame: Uint8Array): TapRequestFrame {
    const decodedData = this.encryptionAlgo.decode(frame);
    const encryptedFrameModel =
      this._encryptedFrameConverter.decode(decodedData);
    const reader = TapStreamReader.fromArray(encryptedFrameModel);
    const tapRequestFrame = reader.readTapRequestFrame();
    return tapRequestFrame;
  }

  private _encryptTapResponse(tapResponse: TapResponseFrame): Uint8Array {
    const encodedTapResponse = TAP_RESPONSE_FRAME_CONVERTER.encode(tapResponse);
    const encryptedFrameModel =
      this._encryptedFrameConverter.encode(encodedTapResponse);
    const encryptedFrame = this.encryptionAlgo.encode(encryptedFrameModel);
    return encryptedFrame;
  }

  @GET({
    path: '/1024//48',
  })
  public secoreCom(context: CallContext<Uint8Array>) {
    let tapRequest: TapRequestFrame;
    try {
      tapRequest = this._decodeEncryptedFrame(context.body);
    } catch (err) {
      console.warn('Failed to decode encrypted frame', (err as Error).message);
      throw TapServerError.notAcceptable();
    }
    const response = context.server.handleRequest(tapRequest, {
      protocol: context.protocol,
    });
    return this._encryptTapResponse(response);
  }

  @GET({
    path: '/1024//45',
  })
  public setInitializationVector(context: CallContext<Uint8Array>): Uint8Array {
    this._updateDecodeInitializationVector(context.body);
    const newResponse = new Uint8Array(INITIALIZATION_VECTOR_LENGTH).fill(0);
    return newResponse;
  }

  private _updateDecodeInitializationVector(iv: Uint8Array) {
    this._options.keys.ivDecode = iv;
    this.refreshEncryptionAlgo();
  }

  @GET({
    path: '/1024//44',
    payloadType: scramConverters.encryptedIVRequest,
  })
  public comWithIv(
    context: CallContext<EncryptedIVRequest>
  ): EncryptedIVResponse {
    const request = context.body.request;
    this._updateDecodeInitializationVector(context.body.iv);
    const response = context.server.handleData(request, {
      protocol: context.protocol,
    });
    console.log('IV UPDATE', response, request);
    const newResponse = new Uint8Array(INITIALIZATION_VECTOR_LENGTH).fill(0);
    return {
      iv: newResponse,
      response: TAP_RESPONSE_FRAME_CONVERTER.encode(response),
    };
  }
}
